'use client';

import { useEffect, useInsertionEffect, useMemo, useRef, useState } from 'react';
import { debounce, type DebouncedFn } from '@funkey/shared/utils/js';
import { type DebouncedFunc, throttle } from 'lodash';

/** Takes in a callback and debounces its calls.
 *
 * @param [delay=500] defaults to `500`ms
 * @see [The original source code](https://usehooks-ts.com/react-hook/use-debounce-callback) for more information. */
export function useDebouncedCallback<T extends (...args: any[]) => ReturnType<T>>(
    func: T,
    delay = 500,
    // Kept for existing callers; `fnRef` always calls the latest `func`, so the instance never needs rebuilding.
    _dependencies: readonly unknown[] = [],
): DebouncedFn<T> {
    const fnRef = useRef<T>(func);
    useInsertionEffect(() => {
        fnRef.current = func;
    });

    const isPending = useRef(false);

    // The instance is built on first call: its callback reads `fnRef`, and handing that to `debounce` during render would read a ref in render.
    const debounced = useMemo(() => {
        const lazy: { instance?: DebouncedFn<T> } = {};
        const getInstance = () =>
            (lazy.instance ??= debounce((...args: Parameters<T>) => {
                isPending.current = false;
                return fnRef.current(...args);
            }, delay));

        const wrappedFunc: DebouncedFn<T> = (...args: Parameters<T>) => {
            isPending.current = true;
            return getInstance()(...args);
        };

        wrappedFunc.cancel = () => lazy.instance?.cancel();
        wrappedFunc.isPending = () => isPending.current;
        wrappedFunc.flush = () => getInstance().flush();

        return wrappedFunc;
    }, [delay]);

    useEffect(() => () => debounced.cancel(), []);

    return debounced;
}

export function useDebouncedValue<T>(value: T | (() => T), delay?: number) {
    const [state, setState] = useState(value);
    const debouncedSetState = useDebouncedCallback(setState, delay);

    useEffect(() => {
        debouncedSetState(value);
    }, [value]);

    return state;
}

export function useThrottledCallback<T extends (...args: any[]) => any>(
    callback: T,
    throttleMs: number,
): DebouncedFunc<T> {
    const ref = useRef<T>(callback);
    useInsertionEffect(() => {
        ref.current = callback;
    });

    // Built on first call for the same reason as in `useDebouncedCallback`.
    const wrappedFunc = useMemo(() => {
        const lazy: { instance?: DebouncedFunc<T> } = {};
        const getInstance = () =>
            (lazy.instance ??= throttle<T>(((...args) => ref.current(...args)) as T, throttleMs, {
                leading: true,
                trailing: true,
            }));

        return Object.assign((...args: Parameters<T>) => getInstance()(...args), {
            cancel: () => lazy.instance?.cancel(),
            flush: () => lazy.instance?.flush(),
        });
    }, [throttleMs]);

    useEffect(() => () => wrappedFunc.cancel(), []);

    return wrappedFunc;
}

/** Returns an internal `state` copy of `factory` that's throttled by `throttleMs` amound of time.
 *
 * If no `dependencies` are passed, it will sync it's state every time the `factory` changes.
 * Esle it will only sync if the `dependencies` values have changed. */
export function useThrottledValue<T>(
    factory: T | (() => T),
    throttleMs: number,
    dependencies?: unknown[],
) {
    const [state, setState] = useState(factory);
    const initialRenderRef = useRef(true);
    const sync = useThrottledCallback(setState, throttleMs);

    useEffect(
        () => {
            if (initialRenderRef.current === true) {
                initialRenderRef.current = false;
                return;
            }

            sync(factory);
        },
        dependencies || [factory],
    );

    return state;
}
